import { Injectable, Injector } from "@angular/core";
import { CartAttributes, RemoveCartItem } from "@models/cart.model";
import { BehaviorSubject, Observable } from "rxjs";
import { CartItemService } from "../cart-item/cart-item.service";

@Injectable({
    providedIn: "root",
})
export class LocalStorageService {
    private keyName = "cartItems";
    private cartItems: CartAttributes[] = [];
    private cartUpdatedSubject = new BehaviorSubject(0);
    public cartUpdateObservable: Observable<number> = this.cartUpdatedSubject.asObservable();
    private cartService!: CartItemService;

    constructor(private injector: Injector) {
        const storedCartItems = localStorage.getItem(this.keyName);
        if (storedCartItems) {
            this.cartItems = JSON.parse(storedCartItems);
        }
    }

    private get cartItemService(): CartItemService {
        if (!this.cartService) {
            this.cartService = this.injector.get(CartItemService);
        }
        return this.cartService;
    }

    public getCartItems(): CartAttributes[] {
        return this.cartItems;
    }

    public addItemToCart(item: CartAttributes): void {
        this.cartItems.push(item);
        this.setCartItems(this.cartItems);
    }

    public setCartItems(items: CartAttributes[]): void {
        this.cartItems = items;
        localStorage.setItem(this.keyName, JSON.stringify(this.cartItems));
        this.cartUpdatedSubject.next(0);
    }

    public getCartedQuantity(productId: number, cartUUID?: string): number {
        const cartItem = this.cartItems.find((item) =>
            item.singleBlockInfo
                ? item.productID === productId && item.cartUUID === cartUUID
                : item.productID === productId,
        );
        return cartItem ? cartItem.quantity : 0;
    }

    public updateCartQuantity(productId: number, quantity: number, cartUUId: string) {
        const index = this.cartItems.findIndex((item) =>
            item.singleBlockInfo
                ? item.productID === productId && item.cartUUID === cartUUId
                : item.productID === productId,
        );

        if (index === -1) {
            return;
        }
        this.cartItems[index].quantity = quantity;
        this.setCartItems(this.cartItems);
    }

    public removeCartItem(productIds: number[], cartUUIDs?: string[]): void {
        this.cartItems = this.cartItems.filter(
            (item) =>
                !productIds.some((id, i) =>
                    cartUUIDs?.[i] ? item.productID === id && item.cartUUID === cartUUIDs[i] : item.productID === id,
                ),
        );

        if (!this.cartItems.length) {
            localStorage.removeItem(this.keyName);
            this.cartUpdatedSubject.next(0);
            return;
        }

        this.setCartItems(this.cartItems);
    }

    public removeAllCartItems(): void {
        this.cartItems = [];
        this.cartItemService.resetCartItems();
        localStorage.removeItem(this.keyName);
        this.cartUpdatedSubject.next(0);
    }
}
